import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Loader2, Package, Search, Clock, Database } from "lucide-react";
import { api, formatPrice } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BRANDS = [
  { v: "all", l: "All brands" },
  { v: "kentucky", l: "Kentucky" },
  { v: "dyon", l: "Dy'on" },
  { v: "velari", l: "Velari" },
  { v: "grooming-deluxe", l: "Grooming Deluxe" },
];

export const InventoryManager = () => {
  const [status, setStatus] = useState(null);
  const [hour, setHour] = useState(0);
  const [minute, setMinute] = useState(0);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const pollRef = useRef(null);

  // inventory table state
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [brand, setBrand] = useState("all");
  const [stock, setStock] = useState("all");
  const [loadingRows, setLoadingRows] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/catalog/sync/status");
      setStatus(data);
      if (data?.settings) { setHour(data.settings.hour); setMinute(data.settings.minute); }
      if (!data?.live?.running && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return data;
    } catch { return null; }
  }, []);

  const fetchRows = useCallback(async () => {
    setLoadingRows(true);
    try {
      const params = { page, limit: 25 };
      if (search) params.search = search;
      if (brand !== "all") params.brand = brand;
      if (stock !== "all") params.stock = stock;
      const { data } = await api.get("/admin/catalog/inventory", { params });
      setRows(data.rows || []); setTotal(data.total || 0); setPages(data.pages || 1);
    } catch { toast.error("Failed to load inventory"); }
    finally { setLoadingRows(false); }
  }, [page, search, brand, stock]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);
  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const d = await fetchStatus();
      if (d && !d.live?.running) fetchRows();
    }, 3000);
  };

  const syncNow = async () => {
    try {
      const { data } = await api.post("/admin/catalog/sync");
      if (data.started) { toast.success("Sync started — runs in the background"); startPolling(); fetchStatus(); }
      else toast.message("A sync is already running"); 
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start sync"); }
  };

  const saveSchedule = async () => {
    setSavingSchedule(true);
    try {
      await api.post("/admin/catalog/schedule", { hour: Number(hour), minute: Number(minute), enabled: true });
      toast.success(`Daily sync set for ${pad(hour)}:${pad(minute)} Irish time`);
    } catch { toast.error("Could not save schedule"); }
    finally { setSavingSchedule(false); }
  };

  const live = status?.live;
  const meta = status?.meta;
  const running = live?.running;
  const pct = live?.total ? Math.round((100 * live.processed) / live.total) : 0;

  return (
    <div className="space-y-4" data-testid="inventory-manager">
      {/* Sync control card */}
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary"><Database className="h-5 w-5" /></span>
            <div>
              <h3 className="text-base font-semibold">Catalogue sync</h3>
              <p className="text-sm text-muted-foreground">Mirror the full Shopify store into the site database. Read-only — never changes Shopify.</p>
            </div>
          </div>
          <Button onClick={syncNow} disabled={running} data-testid="sync-now-button">
            {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            {running ? "Syncing…" : "Sync now"}
          </Button>
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-3">
          <Stat icon={Package} label="Products in database" value={status?.products_in_db ?? "—"} />
          <Stat icon={Clock} label="Last sync"
            value={meta?.last_run_at ? new Date(meta.last_run_at).toLocaleString() : "Never"} />
          <Stat icon={RefreshCw} label="Last run result"
            value={meta?.counts ? `+${meta.counts.new} new · ${meta.counts.updated} upd · ${meta.counts.skipped} skip` : "—"} />
        </div>

        {running && (
          <div className="mt-4" data-testid="sync-progress">
            <div className="mb-1.5 flex justify-between text-xs text-muted-foreground">
              <span>{live.phase}…</span><span className="font-mono">{live.processed}/{live.total} ({pct}%)</span>
            </div>
            <Progress value={pct} className="h-2" />
          </div>
        )}
        {meta?.ok === false && meta?.last_error && (
          <p className="mt-3 text-xs text-destructive">Last sync error: {meta.last_error}</p>
        )}

        {/* Schedule */}
        <div className="mt-5 flex flex-wrap items-end gap-3 border-t border-border pt-5">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Daily auto-sync (Irish time)</label>
            <div className="mt-1.5 flex items-center gap-2">
              <Input type="number" min={0} max={23} value={hour} onChange={(e) => setHour(e.target.value)}
                className="w-20" data-testid="schedule-hour" aria-label="Hour" />
              <span className="text-muted-foreground">:</span>
              <Input type="number" min={0} max={59} value={minute} onChange={(e) => setMinute(e.target.value)}
                className="w-20" data-testid="schedule-minute" aria-label="Minute" />
              <Button variant="secondary" onClick={saveSchedule} disabled={savingSchedule} data-testid="save-schedule-button">
                {savingSchedule ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Only new / changed products are written; unchanged ones are skipped.</p>
        </div>
      </div>

      {/* Inventory table */}
      <div className="rounded-xl border border-border bg-card">
        <div className="flex flex-wrap items-center gap-3 border-b border-border p-4">
          <h3 className="mr-auto text-sm font-semibold">Inventory <span className="text-muted-foreground">({total})</span></h3>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search title / handle" value={search}
              onChange={(e) => { setPage(1); setSearch(e.target.value); }}
              className="h-9 w-56 pl-8" data-testid="inventory-search" />
          </div>
          <Select value={brand} onValueChange={(v) => { setPage(1); setBrand(v); }}>
            <SelectTrigger className="h-9 w-40" data-testid="inventory-brand-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{BRANDS.map((b) => <SelectItem key={b.v} value={b.v}>{b.l}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={stock} onValueChange={(v) => { setPage(1); setStock(v); }}>
            <SelectTrigger className="h-9 w-36" data-testid="inventory-stock-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stock</SelectItem>
              <SelectItem value="in">In stock</SelectItem>
              <SelectItem value="out">Out of stock</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead><TableHead>Brand</TableHead>
                <TableHead>Type</TableHead><TableHead>Price</TableHead><TableHead>Stock</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loadingRows ? (
                <TableRow><TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">Loading…</TableCell></TableRow>
              ) : rows.length ? rows.map((r) => (
                <TableRow key={r.handle} data-testid={`inventory-row-${r.handle}`}>
                  <TableCell className="flex items-center gap-2 max-w-[320px]">
                    {r.image && <img src={r.image} alt="" className="h-9 w-9 rounded object-cover bg-secondary" loading="lazy" />}
                    <span className="truncate text-xs font-medium">{r.title}</span>
                  </TableCell>
                  <TableCell className="text-xs capitalize">{r.brand_slug || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.product_type || "—"}</TableCell>
                  <TableCell className="text-xs">{r.price_min ? formatPrice(r.price_min) : "—"}</TableCell>
                  <TableCell>
                    {r.available
                      ? <Badge className="bg-chart-2 text-white hover:bg-chart-2">In stock</Badge>
                      : <Badge variant="secondary">Out of stock</Badge>}
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow><TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">No products. Run a sync to populate.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        {pages > 1 && (
          <div className="flex items-center justify-between border-t border-border p-3 text-xs">
            <span className="text-muted-foreground">Page {page} of {pages}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
              <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="inventory-next-page">Next</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const pad = (n) => String(n).padStart(2, "0");

const Stat = ({ icon: Icon, label, value }) => (
  <div className="rounded-lg border border-border bg-background/50 p-3">
    <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Icon className="h-3.5 w-3.5" />{label}</div>
    <p className="mt-1 text-sm font-semibold">{value}</p>
  </div>
);
